import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Loader2, ListChecks, CalendarClock, Send, Zap, Clock, ArrowRight, Users } from "lucide-react";

const scoreColor = (v) => (v >= 85 ? "#10B981" : v >= 70 ? "#0055FF" : v >= 50 ? "#F59E0B" : "#EF4444");
const REC_STYLE = { "Client Review": "bg-indigo-100 text-indigo-700", Shortlisted: "bg-violet-100 text-violet-700", "Client Interview": "bg-amber-100 text-amber-700" };
const Card = ({ children, className = "" }) => <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>;

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4"><span className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: accent + "1A" }}><Icon className="h-4.5 w-4.5" style={{ color: accent }} /></span>
      <div className="num font-bold text-2xl text-ink mt-2">{value}</div><div className="text-[11px] font-semibold text-ink-faint">{label}</div></Card>
  );
}

export default function AgencyWorkQueue() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/agency/work-queue").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  const k = d.kpis;
  const openCand = (id) => navigate(`/agency/candidates/${id}`);

  return (
    <div className="space-y-6" data-testid="agency-work-queue">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">{d.scope === "mine" ? "My Work" : "Team Work Queue"}</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Work Queue</h1>
        <p className="text-sm text-ink-soft mt-1">{d.scope === "mine" ? "Your assigned candidates and the next actions to move them forward." : "Everything across the team that needs attention."}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi icon={Users} label={d.scope === "mine" ? "My Candidates" : "Candidates"} value={k.my_candidates} accent="#0055FF" />
        <Kpi icon={CalendarClock} label="Upcoming Interviews" value={k.upcoming_interviews} accent="#8B5CF6" />
        <Kpi icon={ListChecks} label="Awaiting Action" value={k.awaiting_action} accent="#F59E0B" />
        <Kpi icon={Zap} label="Ready to Score" value={k.to_score} accent="#EF4444" />
        <Kpi icon={Send} label="Ready to Recommend" value={k.to_recommend} accent="#10B981" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><ListChecks className="h-4 w-4 text-brand" /> Awaiting Your Action</div>
          <div className="space-y-2">
            {d.awaiting_action.map((r) => (
              <button key={r.recommendation_id} onClick={() => openCand(r.candidate_id)} data-testid={`wq-action-${r.recommendation_id}`} className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:border-brand transition-colors text-left">
                <div><div className="text-sm font-semibold text-ink">{r.candidate_name}</div><div className="text-[11px] text-ink-faint">{r.client_name}</div></div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${REC_STYLE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
              </button>
            ))}
            {d.awaiting_action.length === 0 && <div className="text-sm text-ink-soft text-center py-6">Nothing awaiting action. 🎉</div>}
          </div>
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-brand" /> Upcoming Interviews</div>
          <div className="space-y-2">
            {d.upcoming_interviews.map((iv) => (
              <div key={iv.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3">
                <div><div className="text-sm font-semibold text-ink">{iv.candidate_name}</div><div className="text-[11px] text-ink-faint">{iv.mode_icon} {iv.mode_label} · {iv.role_name}</div></div>
                <div className="text-[11px] num text-ink-soft flex items-center gap-1"><Clock className="h-3 w-3" /> {iv.date} {iv.time}</div>
              </div>
            ))}
            {d.upcoming_interviews.length === 0 && <div className="text-sm text-ink-soft text-center py-6">No upcoming interviews.</div>}
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4"><div className="font-heading font-bold text-ink flex items-center gap-2"><Zap className="h-4 w-4 text-brand" /> Ready to Score</div><button onClick={() => navigate("/agency/interviews")} className="text-xs font-bold text-brand inline-flex items-center gap-1">Assign <ArrowRight className="h-3 w-3" /></button></div>
          <div className="space-y-2">
            {d.to_score.map((c) => (
              <button key={c.id} onClick={() => openCand(c.id)} data-testid={`wq-score-${c.id}`} className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:border-brand transition-colors text-left">
                <div className="text-sm font-semibold text-ink">{c.name}</div><div className="text-[11px] text-ink-faint">{c.target_role_name || "—"}</div>
              </button>
            ))}
            {d.to_score.length === 0 && <div className="text-sm text-ink-soft text-center py-6">All candidates scored.</div>}
          </div>
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Send className="h-4 w-4 text-brand" /> Ready to Recommend</div>
          <div className="space-y-2">
            {d.to_recommend.map((c) => (
              <button key={c.id} onClick={() => openCand(c.id)} data-testid={`wq-recommend-${c.id}`} className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:border-brand transition-colors text-left">
                <div><div className="text-sm font-semibold text-ink">{c.name}</div><div className="text-[11px] text-ink-faint">{c.readiness_level}</div></div>
                {c.einstein_score != null && <span className="num font-extrabold text-lg" style={{ color: scoreColor(c.einstein_score) }}>{c.einstein_score}</span>}
              </button>
            ))}
            {d.to_recommend.length === 0 && <div className="text-sm text-ink-soft text-center py-6">Nothing pending recommendation.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
